import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import type { GetProp } from "../../../../../utils/local-antd";
import { Checkbox, Drawer, Tooltip } from "../../../../../utils/local-antd";
import { Input } from "../../../../../components/Input";
import { useAppDispatch, useAppSelector } from "../../../../../redux/store";
import { userSliceSelectors } from "../../../../../redux/user/user.selector";
import { AdminCreatePSADto } from "../../../../../model/admin/dto/AdminCreatePSA";
import { AtiraToast } from "../../../../../utils/AtiraToast";
import { DrawerDefaultsExtra } from "../../../../tasks/components/DrawerDefaultsExtra";
import { Flex } from "../../../../../components/Flex";
import { Lengths } from "../../../../../model/shared/enum/Lengths.enum";
import { Textarea } from "../../../../../components/Textarea";
import { ImageUpload } from "../../../../../components/ImageUpload";
import { AtiraCheckBoxGroup } from "../../../../../components/AtiraCheckBoxGroup";
import { AtiraSpacePlanType } from "../../../../../model/plan/types/AtiraSpacePlanType.enum";
import { adminActions } from "../../../../../redux/admin/admin.slice";
import { Spacing } from "../../../../../theme/Spacing";
import { Text } from "../../../../../components/Text";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { onSetTypedCRUDErrors } from "../../../../../utils/onSetTypedCRUDErrors";

const StyledAtiraCheckBoxGroup = styled(AtiraCheckBoxGroup)`
  flex-direction: column;
  gap: ${Spacing.s};

  .ant-checkbox-label {
    width: fit-content !important;
  }
`;

const StyledInput = styled(Input)`
  height: 2.5rem;
  border: 2px solid ${(props) => props.theme.main};
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox .ant-checkbox-inner {
    width: 1.5rem;
    height: 1.5rem;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    width: 1.5rem;
    height: 1.5rem;
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    transform: rotate(45deg) scale(1) translate(-50%, -70%);
  }
`;

const ImageUploadWrapper = styled(Flex)`
  & > div {
    width: 50%;
  }
`;

interface TaskCreateDrawerProps {
  open: boolean;
  onClose: VoidFunction;
}

export const AdminPSACreateDrawer: React.FC<TaskCreateDrawerProps> = ({
  open = false,
  onClose,
}) => {
  const [psaCreateLoading, setPsaCreateLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const { control, getValues, setValue, reset, handleSubmit, watch, setError } =
    useForm<AdminCreatePSADto>();
  const isPublic = watch("isPublic");

  const createPSA = async () => {
    try {
      setPsaCreateLoading(true);

      await dispatch(adminActions.createPSA(getValues())).unwrap();
      await dispatch(adminActions.getPSAs({ userId })).unwrap();

      localOnClose();
      AtiraToast.success(t("admin.psa.drawer.create.success"));
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setPsaCreateLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    reset({
      description: "",
      title: "",
      images: [],
      target: Object.values(AtiraSpacePlanType),
      userId,
      isPublic: false,
    });
  }, [reset, userId]);

  const localOnClose = () => {
    onClose();
    resetForm();
  };

  const onTargetChange: GetProp<typeof Checkbox.Group, "onChange"> = async (
    checkedValues,
  ) => {
    setValue("target", checkedValues as AtiraSpacePlanType[]);
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  return (
    <Drawer
      title={t("admin.psa.drawer.create.title")}
      open={open}
      onClose={localOnClose}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(createPSA)}
          onCancel={localOnClose}
          config={{ loading: psaCreateLoading }}
        />
      }
      maskClosable={false}
      size="large"
    >
      <Flex flexDirection="column" gap="m">
        <Controller
          control={control}
          name="title"
          rules={{
            required: {
              value: true,
              message: t("common.this_field_required"),
            },
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t("error.length.less", {
                name: t("common.title"),
                length: Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledInput
              value={value}
              onChange={onChange}
              title={t("common.title")}
              required
              valid={!error}
              errorMessage={error?.message}
              maxLength={Lengths.DESCRIPTION}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          rules={{
            required: false,
            maxLength: {
              value: Lengths.VERY_LONG,
              message: t("error.length.less", {
                name: t("common.description"),
                length: Lengths.VERY_LONG,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Textarea
              value={value}
              onChange={onChange}
              title={t("common.description")}
              valid={!error}
              errorMessage={error?.message}
              maxLength={Lengths.VERY_LONG}
            />
          )}
        />

        <Controller
          control={control}
          name={`images.${0}` as const}
          render={({ field: { value, onChange } }) => (
            <ImageUploadWrapper>
              <ImageUpload
                onChange={(f) => onChange(f)}
                image={value}
                title={t("common.image")}
                showAspectRatioTooltip={false}
                listType="picture-card"
              />
            </ImageUploadWrapper>
          )}
        />

        <Controller
          control={control}
          name="isPublic"
          render={({ field: { value, onChange } }) => (
            <Flex alignItems="center" marginBlock="s">
              <StyledCheckbox
                checked={value}
                value={value}
                onChange={(v) => {
                  onChange(v);
                  if (v) {
                    setValue("target", Object.values(AtiraSpacePlanType));
                  }
                }}
              >
                <Text> {t("admin.psa.drawer.create.public.title")}</Text>
              </StyledCheckbox>

              <Tooltip title={t("admin.psa.drawer.create.public.info")}>
                <span>
                  <AtiraIcon
                    icon={faInfoCircle}
                    color="main"
                    size="sm"
                    cursor="pointer"
                  />
                </span>
              </Tooltip>
            </Flex>
          )}
        />

        {!isPublic ? (
          <Controller
            control={control}
            name={"target"}
            rules={{
              required: {
                value: true,
                message: t("common.this_field_required"),
              },
            }}
            render={({ field: { value }, fieldState: { error } }) => (
              <Flex flexDirection="column" gap="s">
                <Flex flexDirection="column" marginBottom="s">
                  <Flex alignItems="center" gap="s">
                    <Text
                      style={{
                        borderBottom: error
                          ? `1px solid ${theme.danger}`
                          : "none",
                      }}
                    >
                      {t("admin.psa.drawer.create.target.title")}
                    </Text>
                    <Text color="danger"> *</Text>
                  </Flex>
                  <Text fontSize="s" color="darkerSub">
                    {t("admin.psa.drawer.create.target.description")}
                  </Text>
                </Flex>
                <StyledAtiraCheckBoxGroup
                  options={Object.keys(AtiraSpacePlanType).map((planType) => ({
                    label: t(
                      `admin.psa.drawer.create.target.${planType.toLowerCase()}`,
                    ),
                    value: planType,
                  }))}
                  value={value}
                  onChange={onTargetChange}
                  truncate={false}
                  valid={!error}
                  errorMessage={error?.message}
                />
              </Flex>
            )}
          />
        ) : null}
      </Flex>
    </Drawer>
  );
};
